// src/components/Shared/WrapperLoginRequired.jsx
import React, { useMemo, useState, cloneElement, isValidElement } from 'react'
import { observer } from 'mobx-react-lite'
import PropTypes from 'prop-types'
import { Button } from 'antd'
import { useStores } from '../../cls/stores/MobXProvider'
import LoginModal from '../Shared/LoginModal'

const toEventName = (action) =>
	action
		? action.startsWith('on')
			? action.slice(2).toLowerCase()
			: action.toLowerCase()
		: null

/**
 * WrapperLoginRequired is a higher-order component that wraps its children
 * and ensures that the user is logged in before allowing interaction.
 * If the user is not logged in, it intercepts the specified action (e.g., click)
 * and opens a login modal instead.
 * Props:
 * - target: (string) Identifier for the target element (optional).
 * - action: (string) The event name to intercept (e.g., 'onClick').
 * - children: (ReactNode) The child component(s) to wrap.
 * - onLoggedIn: (function) Callback invoked after successful login.
 * - modalProps: (object) Additional props to pass to the LoginModal.
 *
 * Usage example:
	<WrapperLoginRequired onLoggedIn={() => console.log('Logged in')} target={id} action="onClick" >
		<CustomButton id={id}>{label}</CustomButton>
	</WrapperLoginRequired>
 */
const WrapperLoginRequired = ({
	target,
	action,
	children,
	onLoggedIn,
	modalProps
}) => {
	const { users } = useStores()
	const { isLoggedIn } = users
	const [open, setOpen] = useState(false)

	const eventName = useMemo(() => toEventName(action), [action])

	if (isLoggedIn) return <>{children}</>

	// Intercept click at React level
	if (eventName === 'click' && isValidElement(children)) {
		const intercept = (e) => {
			try {
				if (e?.cancelable) e.preventDefault()
				e?.stopPropagation()
			} catch {}
			setOpen(true)
		}
		const cloned = cloneElement(children, {
			onClick: intercept,
			onAuxClick: intercept
		})
		return (
			<>
				{cloned}
				<LoginModal
					title="** Login is required for this action **"
					open={open}
					onClose={() => setOpen(false)}
					onLoggedIn={onLoggedIn}
					modalProps={modalProps}
				/>
			</>
		)
	}

	// Replace-only inline gate when target && !action
	if (target && !action) {
		return (
			<>
				<Button type="primary" onClick={() => setOpen(true)}>
					** Login to continue **
				</Button>
				<LoginModal
					title="** Login is required for this action **"
					open={open}
					onClose={() => setOpen(false)}
					onLoggedIn={onLoggedIn}
					modalProps={modalProps}
				/>
			</>
		)
	}

	// Generic inline gate (no target/action)
	return (
		<>
			<Button type="primary" onClick={() => setOpen(true)}>
				** Login to continue **
			</Button>
			<LoginModal
				title="** Login is required for this action **"
				open={open}
				onClose={() => setOpen(false)}
				onLoggedIn={onLoggedIn}
				modalProps={modalProps}
			/>
		</>
	)
}
WrapperLoginRequired.displayName = 'WrapperLoginRequired'
WrapperLoginRequired.propTypes = {
	target: PropTypes.string,
	action: PropTypes.string, // e.g., 'onClick'
	children: PropTypes.node.isRequired,
	onLoggedIn: PropTypes.func, // callback after successful login
	modalProps: PropTypes.object // forwarded to AntD Modal
}

export default observer(WrapperLoginRequired)
